import React from 'react';
import { Box } from '@mui/material';
import { DataTable, Panel, type DataTableColumn } from '../../../../ui-kit';
import type { ReportPayload } from '../../../../types/reportDashboard';
import { money } from '../reportFormat';
import type { ReportDownload } from '../useReportDownload';
import type { ReportQuery } from '../useReportCohort';
import { useReportDates } from '../useReportDates';

interface BandRow {
  label: string;
  inForcePolicies: number;
  inForcePremium: number;
  lapsedPolicies: number;
  lapsedPremium: number;
}

/**
 * Her PAID and INACTIVE reports in one table: how long policies have been paid
 * for — 1m, 2m, 3m-12m, years 2 to 5, then five-year bands — with the policies
 * still in force beside the ones that lapsed.
 *
 * Side by side because that is the comparison worth having: 512 policies lapsed
 * inside their first month against 104 still being paid that long. Both count
 * every policy on the books, whatever month the application was sent, since how
 * long a policy lasts is only seen over years. The board's filters still apply.
 */
export default function PersistencyPanel({ payload, query, download, loading }: {
  payload: ReportPayload;
  query: ReportQuery;
  download: ReportDownload;
  loading?: boolean;
}) {
  const block = 'persistency';
  const dates = useReportDates(payload, query, payload.paid.dates, 'all');
  const { paid, inactive } = dates.source;
  const lapsedByBand = new Map(inactive.bands.map((b) => [b.label, b]));
  const rows: BandRow[] = paid.bands.map((b) => ({
    label: b.label,
    inForcePolicies: b.policies,
    inForcePremium: b.premium,
    lapsedPolicies: lapsedByBand.get(b.label)?.policies ?? 0,
    lapsedPremium: lapsedByBand.get(b.label)?.premium ?? 0,
  }));

  const columns: DataTableColumn<BandRow>[] = [
    { key: 'band', header: paid.band_term.label, render: (r) => r.label },
    { key: 'paid_policies', header: 'Policies paid', numeric: true, render: (r) => r.inForcePolicies.toLocaleString('en-US') },
    { key: 'paid_premium', header: 'Premium paid', numeric: true, render: (r) => money(r.inForcePremium) },
    { key: 'lapsed_policies', header: 'Policies lapsed', numeric: true, render: (r) => r.lapsedPolicies.toLocaleString('en-US') },
    { key: 'lapsed_premium', header: 'Premium lapsed', numeric: true, render: (r) => money(r.lapsedPremium) },
  ];

  const body = (fill: boolean) => (
    <DataTable
      ariaLabel="Paid and inactive policies by how long they were paid for"
      columns={columns}
      groups={[{ key: 'bands', title: '', rows }]}
      getRowKey={(r) => r.label}
      collapsibleGroups={false}
      columnPicker={false}
      fill={fill}
    />
  );

  const startedIn = new Date(`${paid.history_start}T00:00:00`).toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
  // What the panel is counting, in the same words as its Dates select.
  const counted = dates.basis === 'all'
    ? 'whenever the application was sent'
    : `for the ${(payload.paid.dates.find((d) => d.key === dates.basis)?.label ?? '').toLowerCase()} in the range above`;
  const notes = [...paid.notes, ...inactive.notes];
  // One icon for the panel, not one per note: what the bands count, then what
  // the data cannot tell us.
  const info = (
    <>
      {paid.band_term.definition}
      {notes.map((n) => (
        <Box key={n.key} component="span" sx={{ display: 'block', mt: 1.5 }}>
          <Box component="span" sx={{ display: 'block', fontWeight: 600 }}>{n.label}</Box>
          {n.missing ?? n.proxy}
        </Box>
      ))}
    </>
  );

  return (
    <Panel
      title="How long policies stay on the books"
      info={info}
      infoTone={notes.some((n) => n.missing ?? n.proxy) ? 'warning' : undefined}
      subtitle={
        `PAID REPORT and INACTIVE REPORT. ${paid.total.toLocaleString('en-US')} policies still being paid, `
        + `${inactive.total.toLocaleString('en-US')} lapsed, ${counted}. `
        + `The Klaris warehouse holds data from ${startedIn} onwards.`
      }
      tools={dates.control}
      actions={download.button(block, { disabled: loading, dateBasis: dates.basis })}
      footer={download.problem(block)}
      loading={loading || dates.reloading}
      loadingHeight={320}
      flush
      expanded={{ content: body(true) }}
    >
      {body(false)}
    </Panel>
  );
}
